import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Landmark, ExternalLink, Percent, Save, Info, Download, ShieldCheck, AlertTriangle, Lock } from 'lucide-react';
import {
  getBtwStatus,
  zetBtwStatus,
  getWallet,
  getAfrekeningen,
  openStripeDashboard,
  startStripeOnboarding,
  downloadAfrekening,
  KOR_OMZETGRENS,
  type Afrekening,
  type BtwInstellingen,
  type RolStatus,
} from '../../services/professionalFiscaalApi';

/**
 * Mijn Cockpit → Profiel → Facturen (Onderdeel 56E): Stripe-wallet, btw-status
 * (KOR + per rol) met de wettelijke richtlijnen, en het afrekeningenarchief.
 */

const NS = 'professionalFacturen';

const eur = (n: number, lng: string) =>
  new Intl.NumberFormat(lng.startsWith('en') ? 'en-GB' : 'nl-NL', { style: 'currency', currency: 'EUR' }).format(n);

const foutTekst = (err: unknown) => (err as { response?: { data?: { message?: string } } })?.response?.data?.message;

const Paneel: React.FC<{ icon: React.ElementType; titel: string; children: React.ReactNode }> = ({ icon: Icon, titel, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white shadow-card">
    <header className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
      <Icon className="h-4 w-4 text-navy-700" />
      <h3 className="text-sm font-semibold text-navy-900">{titel}</h3>
    </header>
    <div className="p-5">{children}</div>
  </section>
);

// ── 2.1 Stripe Connect & wallet ─────────────────────────────────────────────

const STATUS_KLEUR: Record<string, string> = {
  verified: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  rejected: 'bg-red-100 text-red-800',
  not_started: 'bg-gray-100 text-gray-700',
};

const WalletPaneel: React.FC = () => {
  const { t, i18n } = useTranslation(NS);
  const { data, isLoading, isError } = useQuery({ queryKey: ['professional-fiscaal-wallet'], queryFn: getWallet });
  const [bezig, setBezig] = useState(false);

  const openStripe = async () => {
    setBezig(true);
    try {
      const url = data?.onboarding_status === 'verified' ? await openStripeDashboard() : await startStripeOnboarding();
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(foutTekst(err) || t(data?.onboarding_status === 'verified' ? 'wallet.dashboardFout' : 'wallet.onboardingFout'));
    } finally {
      setBezig(false);
    }
  };

  return (
    <Paneel icon={Landmark} titel={t('wallet.titel')}>
      {isLoading ? (
        <p className="text-sm text-gray-500">{t('laden')}</p>
      ) : isError || !data ? (
        <p className="text-sm text-red-700">{t('fout')}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-600">{t('wallet.status')}:</span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_KLEUR[data.onboarding_status]}`}>
              {t(`wallet.statussen.${data.onboarding_status}`)}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-canvas p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('wallet.gerealiseerd')}</p>
              <p className="mt-1 text-2xl font-semibold text-navy-900">{eur(data.gerealiseerd, i18n.language)}</p>
              <p className="mt-1 text-xs text-gray-500">{t('wallet.gerealiseerdUitleg')}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-canvas p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('wallet.pending')}</p>
              <p className="mt-1 text-2xl font-semibold text-navy-900">{eur(data.pending, i18n.language)}</p>
              <p className="mt-1 text-xs text-gray-500">{t('wallet.pendingUitleg', { count: data.pending_aantal })}</p>
            </div>
          </div>
          {data.mislukt_aantal > 0 && (
            <p className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {t('wallet.mislukt', { count: data.mislukt_aantal })}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={openStripe}
              disabled={bezig}
              className="inline-flex items-center gap-2 rounded-lg bg-navy-700 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-60"
            >
              <ExternalLink className="h-4 w-4" />
              {t(data.onboarding_status === 'verified' ? 'wallet.dashboard' : 'wallet.onboarding')}
            </button>
            <span className="text-xs text-gray-500">{t('wallet.dashboardUitleg')}</span>
          </div>
        </div>
      )}
    </Paneel>
  );
};

// ── 2.2 Btw-status: KOR + per rol ───────────────────────────────────────────

const BtwPaneel: React.FC<{ data: BtwInstellingen }> = ({ data }) => {
  const { t, i18n } = useTranslation(NS);
  const queryClient = useQueryClient();
  const [kor, setKor] = useState(data.kor_actief);
  const [korSinds, setKorSinds] = useState(data.kor_sinds ?? '');
  const [rollen, setRollen] = useState<Record<string, RolStatus>>({});

  useEffect(() => {
    setKor(data.kor_actief);
    setKorSinds(data.kor_sinds ?? '');
    setRollen(Object.fromEntries(data.rollen.map((r) => [r.professional_type, r.status])));
  }, [data]);

  const gewijzigd = useMemo(
    () =>
      kor !== data.kor_actief ||
      (kor && korSinds !== (data.kor_sinds ?? '')) ||
      data.rollen.some((r) => rollen[r.professional_type] !== r.status),
    [kor, korSinds, rollen, data]
  );

  const opslaan = useMutation({
    mutationFn: () => zetBtwStatus({ kor_actief: kor, kor_sinds: kor && korSinds ? korSinds : null, rollen }),
    onSuccess: (nieuw) => {
      queryClient.setQueryData(['professional-fiscaal-btw'], nieuw);
      toast.success(t('btw.opgeslagen'));
    },
    onError: (err) => toast.error(foutTekst(err) || t('btw.opslaanFout')),
  });

  return (
    <Paneel icon={Percent} titel={t('btw.titel')}>
      <p className="mb-5 text-sm text-gray-600">{t('btw.intro')}</p>

      <div className="mb-6 rounded-lg border border-gray-200 bg-canvas p-4">
        <p className="text-sm font-semibold text-navy-900">{t('btw.kor.titel')}</p>
        <p className="mb-3 mt-1 text-xs text-gray-600">{t('btw.kor.uitleg')}</p>
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-navy-900">
            <input type="checkbox" checked={kor} onChange={(e) => setKor(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-navy-700" />
            {t('btw.kor.label')}
          </label>
          {kor && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              {t('btw.kor.sinds')}
              <input
                type="date"
                value={korSinds}
                onChange={(e) => setKorSinds(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
              />
            </label>
          )}
        </div>
        {kor && data.omzet_dit_jaar > KOR_OMZETGRENS && (
          <p className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-2.5 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            {t('btw.kor.grens', { bedrag: eur(data.omzet_dit_jaar, i18n.language) })}
          </p>
        )}
      </div>

      <p className="mb-2 text-sm font-semibold text-navy-900">{t('btw.rollenTitel')}</p>
      {kor && <p className="mb-3 rounded-md bg-navy-50 p-2.5 text-xs text-navy-800">{t('btw.kor.actiefMelding')}</p>}
      {data.rollen.length === 0 ? (
        <p className="text-sm text-gray-500">{t('btw.geenRollen')}</p>
      ) : (
        <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {data.rollen.map((rol) => (
            <fieldset key={rol.professional_type} disabled={kor} className={`p-4 ${kor ? 'opacity-50' : ''}`}>
              <legend className="sr-only">{rol.label}</legend>
              <p className="mb-2 text-sm font-semibold text-navy-900">{rol.label}</p>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
                {(['21_percent', '0_percent_medical'] as RolStatus[]).map((optie) => {
                  const uit = optie === '0_percent_medical' && !rol.medisch_geverifieerd;
                  return (
                    <label key={optie} className={`inline-flex items-center gap-2 text-sm ${uit ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-800'}`}>
                      <input
                        type="radio"
                        name={`btw-${rol.professional_type}`}
                        value={optie}
                        checked={rollen[rol.professional_type] === optie}
                        disabled={uit}
                        onChange={() => setRollen((huidig) => ({ ...huidig, [rol.professional_type]: optie }))}
                        className="h-4 w-4 border-gray-300 text-navy-700"
                      />
                      {t(`btw.opties.${optie}`)}
                    </label>
                  );
                })}
              </div>
              <p className={`mt-2 flex items-center gap-1.5 text-xs ${rol.medisch_geverifieerd ? 'text-green-700' : 'text-gray-500'}`}>
                {rol.medisch_geverifieerd ? <ShieldCheck className="h-3.5 w-3.5" /> : <Info className="h-3.5 w-3.5" />}
                {t(rol.medisch_geverifieerd ? 'btw.medischBig' : rol.medisch_mogelijk ? 'btw.medischGeenBig' : 'btw.medischNietMogelijk')}
              </p>
            </fieldset>
          ))}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => opslaan.mutate()}
          disabled={!gewijzigd || opslaan.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-navy-700 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {t('btw.opslaan')}
        </button>
        <span className="text-xs text-gray-500">{t('btw.geldigheid')}</span>
      </div>
    </Paneel>
  );
};

// ── Sectie 3: wettelijke richtlijnen ───────────────────────────────────────

const INFO_BLOKKEN = [
  { sleutel: 'regel21', rand: 'border-l-green-500' },
  { sleutel: 'medisch', rand: 'border-l-navy-500' },
  { sleutel: 'kor', rand: 'border-l-amber-500' },
] as const;

const BtwInfo: React.FC = () => {
  const { t } = useTranslation(NS);
  return (
    <Paneel icon={Info} titel={t('info.titel')}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {INFO_BLOKKEN.map(({ sleutel, rand }) => (
          <div key={sleutel} className={`rounded-lg border border-gray-200 border-l-4 ${rand} bg-canvas p-4`}>
            <p className="mb-2 text-sm font-semibold text-navy-900">{t(`info.${sleutel}.titel`)}</p>
            <ul className="list-disc space-y-1.5 pl-4 text-xs text-gray-700">
              {(t(`info.${sleutel}.punten`, { returnObjects: true }) as string[]).map((punt) => (
                <li key={punt}>{punt}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-gray-500">{t('info.disclaimer')}</p>
    </Paneel>
  );
};

// ── 2.3 Facturatiehistorie ─────────────────────────────────────────────────

const ARCHIEF_STATUS_KLEUR: Record<Afrekening['status'], string> = {
  pending: 'bg-amber-100 text-amber-800',
  processing: 'bg-navy-100 text-navy-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

const Archief: React.FC = () => {
  const { t, i18n } = useTranslation(NS);
  const { data, isLoading, isError } = useQuery({ queryKey: ['professional-fiscaal-afrekeningen'], queryFn: getAfrekeningen });
  const datum = (d: string) => new Date(d).toLocaleDateString(i18n.language.startsWith('en') ? 'en-GB' : 'nl-NL');

  const download = async (a: Afrekening, formaat: 'pdf' | 'csv') => {
    try {
      await downloadAfrekening(a, formaat);
    } catch {
      toast.error(t('archief.downloadFout'));
    }
  };

  const btwCel = (a: Afrekening) =>
    a.btw_behandeling === '21_percent'
      ? eur(a.btw_bedrag, i18n.language)
      : a.btw_behandeling === '0_percent_kor'
        ? `${t('archief.vrijgesteld')} (${t('archief.kor')})`
        : t('archief.vrijgesteld');

  return (
    <Paneel icon={Download} titel={t('archief.titel')}>
      <p className="mb-4 text-sm text-gray-600">{t('archief.intro')}</p>
      {isLoading ? (
        <p className="text-sm text-gray-500">{t('laden')}</p>
      ) : isError ? (
        <p className="text-sm text-red-700">{t('fout')}</p>
      ) : !data?.length ? (
        <p className="text-sm text-gray-500">{t('archief.leeg')}</p>
      ) : (
        <div className="-mx-5 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-5 py-2 font-semibold">{t('archief.factuurdatum')}</th>
                <th className="px-3 py-2 font-semibold">{t('archief.nummer')}</th>
                <th className="px-3 py-2 font-semibold">{t('archief.opdrachtgever')}</th>
                <th className="px-3 py-2 text-right font-semibold">{t('archief.excl')}</th>
                <th className="px-3 py-2 text-right font-semibold">{t('archief.btw')}</th>
                <th className="px-3 py-2 text-right font-semibold">{t('archief.totaal')}</th>
                <th className="px-3 py-2 font-semibold">{t('archief.status')}</th>
                <th className="px-5 py-2 font-semibold">{t('archief.download')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((a) => (
                <tr key={a.id}>
                  <td className="whitespace-nowrap px-5 py-2.5 text-gray-700">{datum(a.factuurdatum)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-medium text-navy-900">{a.nummer}</td>
                  <td className="px-3 py-2.5 text-gray-700">
                    {a.opdrachtgever}
                    {a.pool && <span className="block text-xs text-gray-500">{a.pool} · {a.periode}</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right text-gray-800">{eur(a.netto, i18n.language)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right text-gray-800">{btwCel(a)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold text-navy-900">{eur(a.incl_btw, i18n.language)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ARCHIEF_STATUS_KLEUR[a.status]}`}>{t(`archief.statussen.${a.status}`)}</span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-2.5">
                    <div className="flex gap-2">
                      {(['pdf', 'csv'] as const).map((f) => (
                        <button
                          key={f}
                          type="button"
                          onClick={() => download(a, f)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-navy-800 hover:bg-gray-50"
                        >
                          <Download className="h-3.5 w-3.5" />
                          {f.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Paneel>
  );
};

export const ProfielFacturenTab: React.FC = () => {
  const { t } = useTranslation(NS);
  const btw = useQuery({ queryKey: ['professional-fiscaal-btw'], queryFn: getBtwStatus });

  return (
    <div className="space-y-6 rounded-2xl bg-canvas p-4 sm:p-6">
      <p className="flex items-start gap-2 text-xs text-gray-600">
        <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        {t('privacy')}
      </p>
      <WalletPaneel />
      {btw.isLoading ? (
        <p className="text-sm text-gray-500">{t('laden')}</p>
      ) : btw.isError || !btw.data ? (
        <p className="text-sm text-red-700">{t('fout')}</p>
      ) : (
        <BtwPaneel data={btw.data} />
      )}
      <BtwInfo />
      <Archief />
    </div>
  );
};

export default ProfielFacturenTab;
